import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

import { API_CREATE_ROOMSLOT, API_GET_ROOMSLOTS } from "../../Util/URLConstants";
import Loader from "../../Components/Loader";
import { VM_Colors } from "../../Styles/VMStyles";

/* ---------- SLOT BLUEPRINTS ---------- */

const DEFAULT_12HR = [
  { slot_name: "Slot 1", check_in: "", check_out: "", is_next_day: false },
  { slot_name: "Slot 2", check_in: "", check_out: "", is_next_day: false },
];

const DEFAULT_24HR = [
  { slot_name: "Slot Timings", check_in: "", check_out: "", is_next_day: false },
];

const RoomSlots = ({ onSuccess, user_id }) => {
  const [loading, setLoading] = useState(true); // 🔑 start true
  const [format, setFormat] = useState("12hr");
  const [fieldErrors, setFieldErrors] = useState({});

  // Separate cache for each format
  const [slots12hr, setSlots12hr] = useState(null); // 🔑 null initially
  const [slots24hr, setSlots24hr] = useState(null);

  /* ---------- FETCH EXISTING SLOTS ---------- */

  useEffect(() => {
    const fetchTimeSlots = async () => {
      try {
        const resp = await axios.get(
          `${API_GET_ROOMSLOTS}?user_id=${user_id}`
        );

        if (resp.data?.status_code === 200) {
          const slotData = resp.data.slots || [];

          // ✅ Existing 24hr slot
          if (slotData.length > 0 && slotData[0].slot_type?.includes("24")) {
            setFormat("24hr");
            setSlots24hr([
              {
                slot_name: slotData[0].slot_name,
                check_in: slotData[0].check_in,
                check_out: slotData[0].check_out,
                is_next_day: slotData[0].is_next_day,
              },
            ]);
            setSlots12hr([...DEFAULT_12HR]); // keep fallback
          }
          // ✅ Existing 12hr slots
          else if (slotData.length > 0) {
            setFormat("12hr");
            const updated = slotData.map((s, i) => ({
              slot_name: s.slot_name || `Slot ${i + 1}`,
              check_in: s.check_in,
              check_out: s.check_out,
              is_next_day: s.is_next_day,
            }));

            if (updated.length === 1) {
              updated.push({ ...DEFAULT_12HR[1] });
            }

            setSlots12hr(updated);
            setSlots24hr([...DEFAULT_24HR]);
          }
          // ✅ No slots exist
          else {
            setFormat("12hr");
            setSlots12hr([...DEFAULT_12HR]);
            setSlots24hr([...DEFAULT_24HR]);
          }
        }
      } catch {
        toast.error("Failed to fetch slots");
        setSlots12hr([...DEFAULT_12HR]);
        setSlots24hr([...DEFAULT_24HR]);
      } finally {
        setLoading(false); // 🔑 allow render
      }
    };

    fetchTimeSlots();
  }, [user_id]);

  /* ---------- ACTIVE SLOTS ---------- */

  const activeSlots =
    format === "12hr" ? slots12hr : slots24hr;

  const setActiveSlots =
    format === "12hr" ? setSlots12hr : setSlots24hr;

  /* ---------- HANDLERS ---------- */

  const handleFormatChange = (e) => {
    setFormat(e.target.value);
    setFieldErrors({});
  };

  const handleSlotChange = (index, field, value) => {
    const updated = [...activeSlots];
    updated[index][field] = value;

    if (updated[index].check_in && updated[index].check_out) {
      updated[index].is_next_day =
        updated[index].check_in > updated[index].check_out;
    }

    setActiveSlots(updated);
    setFieldErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const nextSlotErrors = { ...prev[index], [field]: "" };
      return { ...prev, [index]: nextSlotErrors };
    });
  };

  const validateSlots = () => {
    const errors = {};

    activeSlots.forEach((slot, index) => {
      const slotErrors = {};
      if (!slot.check_in) slotErrors.check_in = "Required";
      if (!slot.check_out) slotErrors.check_out = "Required";
      if (Object.keys(slotErrors).length) {
        errors[index] = slotErrors;
      }
    });

    setFieldErrors(errors);

    if (Object.keys(errors).length) {
      if (format === "12hr") {
        toast.warning("Please enter Check-In and Check-Out for both Slot 1 and Slot 2.");
      } else {
        toast.warning("Please enter Check-In and Check-Out.");
      }
      return false;
    }

    return true;
  };

  /* ---------- SUBMIT (PAYLOAD UNCHANGED) ---------- */

  const handleSubmit = async () => {
    if (!validateSlots()) return;

    const filledSlots = activeSlots.filter(
      (s) => s.check_in && s.check_out
    );

    try {
      setLoading(true);

      const payload = {
        user_id,
        slot_type: format,
        slots: filledSlots.map((s) => ({
          slot_name: s.slot_name,
          check_in: s.check_in,
          check_out: s.check_out,
          is_next_day: s.is_next_day,
        })),
      };

      const res = await axios.post(API_CREATE_ROOMSLOT, payload);

      if (res.data.status_code === 200) {
        toast.success("Room slots updated successfully!");
        onSuccess?.();
      } else {
        toast.error(res.data.message || "Failed to save slots");
      }
    } catch {
      toast.error("Failed to save slots");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- LOADING GUARD ---------- */

  if (loading || !activeSlots) {
    return <Loader loading />;
  }

  /* ---------- UI ---------- */

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      {/* Suggested */}
      <Box
        sx={{
          border: "1px dashed #d0d0d0",
          borderRadius: 2,
          p: 2,
          mb: 3,
          backgroundColor: "#FAFAFA",
        }}
      >
        <Chip
          label="Recommended"
          size="small"
          sx={{ bgcolor: VM_Colors.primary, color: "#fff", mb: 1 }}
        />

        {format === "12hr" ? (
          <>
            <Typography fontWeight={600}>12 Hours Slots</Typography>
            <Typography>Slot 1: 8:00 AM – 6:00 PM</Typography>
            <Typography>Slot 2: 8:00 PM – 6:00 AM</Typography>
          </>
        ) : (
          <>
            <Typography fontWeight={600}>24 Hours Stay</Typography>
            <Typography>Check-in: 12:00 PM</Typography>
            <Typography>Check-out: 10:00 AM</Typography>
          </>
        )}
      </Box>

      {/* Format Selector */}
      <FormControl sx={{ mb: 2 }}>
        <Typography fontWeight={700}>Select Time Format</Typography>
        <RadioGroup row value={format} onChange={handleFormatChange}>
          <FormControlLabel value="12hr" control={<Radio />} label="12 Hours" />
          <FormControlLabel value="24hr" control={<Radio />} label="24 Hours" />
        </RadioGroup>
      </FormControl>

      {/* Slots */}
      {activeSlots.map((slot, i) => (
        <Box key={`${format}-${i}`} sx={{ mb: 2 }}>
          <Typography fontWeight={600} sx={{mb:2}}>{slot.slot_name}</Typography>
          <Stack direction="row" spacing={2} mt={1}>
            <TextField
              type="time"
              label="Check-In"
              value={slot.check_in}
              onChange={(e) =>
                handleSlotChange(i, "check_in", e.target.value)
              }
              fullWidth
              required
              error={Boolean(fieldErrors[i]?.check_in)}
              helperText={fieldErrors[i]?.check_in || " "}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              type="time"
              label="Check-Out"
              value={slot.check_out}
              onChange={(e) =>
                handleSlotChange(i, "check_out", e.target.value)
              }
              fullWidth
              required
              error={Boolean(fieldErrors[i]?.check_out)}
              helperText={fieldErrors[i]?.check_out || " "}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </Stack>
          {slot.check_in && slot.check_out && (
            <Typography variant="body2" color="text.secondary">
              Is next day: {slot.is_next_day ? "Yes" : "No"}
            </Typography>
          )}
        </Box>
      ))}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleSubmit}
      >
        Submit
      </Button>
      {
        loading ? <Loader loading={loading} /> : null 
      }
    </Paper>
  );
};

export default RoomSlots;
